'use client';

import { Button } from '@/components/ui/button';
import { useNetworkStatus } from '@/hooks/use-network-status';
import { getPendingFileSaveCopy } from '@/i18n/pending-file-save-copy';
import {
  dismissPendingSave,
  getPendingSave,
  pendingSavePresentation,
  sharePendingSave,
  splitFileExtension,
  subscribePendingSave,
  type PendingSave,
} from '@/lib/save-blob';
import { cn } from '@/lib/utils';
import { FileDown, X } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useSyncExternalStore } from 'react';

const getServerSnapshot = () => null;

/** 36 px buttons keep 44 px tap targets (design system: mobile tap targets). */
const HIT_AREA_44 = "relative after:absolute after:-inset-1 after:content-['']";

/**
 * On iPhone and iPad a download goes to the share sheet. When the file took
 * long enough to fetch that the original tap expired, this prompt holds the
 * file until the member taps again. It stays until they save or dismiss it.
 * It sits at the top, like the offline pill, so it never covers the composer.
 */
export function PendingFileSavePrompt() {
  const pending = useSyncExternalStore(
    subscribePendingSave,
    getPendingSave,
    getServerSnapshot
  );
  return pending ? <PendingFileSaveCard pending={pending} /> : null;
}

/** Mounted only while a file is held, so the network check runs only then. */
function PendingFileSaveCard({ pending }: { pending: PendingSave }) {
  const locale = useLocale();
  const copy = getPendingFileSaveCopy(locale);
  const isOnline = useNetworkStatus();
  const { stem, extension } = splitFileExtension(pending.name);
  const { status, action } = pendingSavePresentation(pending);
  const actionClassName = cn('h-9 shrink-0 rounded-full px-3.5', HIT_AREA_44);

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 z-[100] flex justify-center px-3',
        // Below the offline pill (top-3, 40 px) when both show.
        isOnline
          ? 'top-[max(0.75rem,env(safe-area-inset-top))]'
          : 'top-[calc(max(0.75rem,env(safe-area-inset-top))+3rem)]'
      )}
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl',
          'border-border/70 bg-background/95 text-foreground border p-2.5 pl-3 shadow-lg shadow-black/10 backdrop-blur-xl',
          'supports-[backdrop-filter]:bg-background/80 dark:border-white/10 dark:bg-black/70'
        )}
      >
        <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
          <FileDown className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 text-sm font-medium">
            <span className="truncate">{stem}</span>
            {extension ? <span className="shrink-0">{extension}</span> : null}
          </p>
          <p className="text-muted-foreground text-xs">{copy[status]}</p>
        </div>
        {action === 'share' || action === 'retry' ? (
          <Button
            type="button"
            size="sm"
            className={actionClassName}
            onClick={() => void sharePendingSave()}
          >
            {action === 'share' ? copy.save : copy.retry}
          </Button>
        ) : null}
        {action === 'open' && pending.sourceUrl ? (
          // The file's own URL opens as a real page Safari can save or share.
          <Button asChild size="sm" className={actionClassName}>
            <a
              href={pending.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismissPendingSave}
            >
              {copy.open}
            </a>
          </Button>
        ) : null}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={cn('size-9 shrink-0 rounded-full', HIT_AREA_44)}
          aria-label={copy.dismiss}
          onClick={dismissPendingSave}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
